// The customer's brand (#1382): what GET /brand/:customer answers, read from
// their Brand assets folder or, failing that, the built-in skin catalogue.
// The start screen takes its covers from here; the same answer drives web
// app skins, share cards and the seeded dashboard theme on the server.
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { BrandCover, coversFromBrand, type BrandApiCover } from '../components/agent/customerBackdrops'

export interface BrandApiResponse {
  customer: string
  name: string
  label: string
  source: { kind: 'folder' | 'catalogue'; job?: string; team?: string; team_folder_present?: boolean }
  palette: { primary?: string; series?: string[]; single?: string; brands?: Record<string, string> }
  fonts: { heading?: string; body?: string }
  logos: { onLight?: string; onDark?: string }
  template?: string
  assets: Record<string, string>
  covers: BrandApiCover[]
}

export interface BrandState {
  status: 'idle' | 'loading' | 'loaded' | 'error'
  brand: BrandApiResponse | null
  /** Start-screen covers from the folder; `[]` until loaded or when there are none. */
  covers: BrandCover[]
}

const EMPTY: BrandState = { status: 'idle', brand: null, covers: [] }

export default function useBrand(customer: string | undefined): BrandState {
  const [state, setState] = useState<BrandState>(EMPTY)
  const loadedFor = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!customer || loadedFor.current === customer) return
    loadedFor.current = customer
    let cancelled = false
    setState({ status: 'loading', brand: null, covers: [] })
    axios
      .get<BrandApiResponse>(`${API_BASE_URL}/brand/${encodeURIComponent(customer)}`)
      .then((res) => {
        if (cancelled) return
        const brand = res.data && Array.isArray(res.data.covers) ? res.data : null
        setState({ status: 'loaded', brand, covers: brand ? coversFromBrand(brand.covers) : [] })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', brand: null, covers: [] })
      })
    return () => {
      cancelled = true
    }
  }, [customer])

  return state
}
